import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faStop } from '@fortawesome/free-solid-svg-icons';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useModal } from 'components/modals/ModalContext';
import { useElapsedTime, useTimer } from 'services/timer/TimerContext';
import TimerStartForm from '../../views/timesheets/TimerStartForm';
import TimeRowForm from '../../views/timesheets/TimeRowForm';
import '../../style/scss/navigation.scss';

// The primary timer button in the navbar which starts or stops the timer of the user
const TimerButton: React.FC = () => {
    const { t } = useTranslation();
    const { activeFeatures } = useAuthContext();
    const { environmentSettings } = useSettings();
    const { initializeModal } = useModal();
    const { runningTimer } = useTimer();
    const elapsedTime = useElapsedTime(runningTimer?.start_datetime);

    // Only show the button when office time tracking is enabled or a timer is already running
    const timesheetsActive = activeFeatures.includes('manage_timesheets');
    if (!timesheetsActive || (!environmentSettings.office_time_tracking && !runningTimer)) return null;

    // Open the start form of a new timer
    const handleStart = () => {
        initializeModal(<TimerStartForm />, {
            title: t('timerow.timer.start_title'),
            modalSize: 'add-form-smaller'
        });
    };

    // Open the stop form of the running timer
    const handleStop = () => {
        if (!runningTimer) return;
        initializeModal(<TimeRowForm stopTimer={true} />, {
            title: t('timerow.timer.stop_title'),
            itemId: runningTimer.id,
            modalSize: 'add-form-smaller'
        });
    };

    // The description of what the timer runs on, shown in the tooltip
    const runningOn = runningTimer?.ticket
        ? t('ticket.general.ticket_title', { number: runningTimer.ticket.number })
        : runningTimer?.job?.number
            ? `${t('timerow.general.job_label')} ${runningTimer.job.number}`
            : runningTimer?.project?.name || runningTimer?.time_type?.name || '';

    return (
        <button type='button'
                className={`button button-extra-small button-primary timer-button ${runningTimer ? 'timer-running' : ''}`}
                onClick={runningTimer ? handleStop : handleStart}>
            <FontAwesomeIcon icon={runningTimer ? faStop : faPlay} className='timer-icon' />
            <span className='timer-label'>
                {runningTimer ? elapsedTime : t('timerow.timer.start_button_label')}
            </span>
            <span className='timer-tooltip'>
                {runningTimer ? t('timerow.timer.stop_tooltip', { subject: runningOn }) : t('timerow.timer.start_tooltip')}
            </span>
        </button>
    );
};

export default TimerButton;
